<script setup lang="ts">
import {
  SCHEDULE_STATUS_OPEN,
  isClosedSlotStatus,
} from '@/entities/appointment/lib/scheduleStatus';
import { TIME_SLOTS_SHOW_COUNT } from '@/entities/appointment/lib/appointmentConstants';
import type { ITimeSlot } from '@/entities/appointment/model/types';

const SHOW_COUNT = TIME_SLOTS_SHOW_COUNT;

const props = withDefaults(
  defineProps<{
    isCalendar?: boolean;
    timestamps?: ITimeSlot[];
    selectedTime?: string;
  }>(),
  {
    isCalendar: false,
    timestamps: () => [],
    selectedTime: '',
  },
);

const emit = defineEmits<{ (e: 'select', time: string): void }>();

const isMobile: boolean = useIsMobile();
const checkedTime: Ref<string> = ref(props.selectedTime);
const isCollapsed: Ref<boolean> = ref(props.timestamps.length > SHOW_COUNT);

const times: ComputedRef<ITimeSlot[]> = computed(() =>
  isCollapsed.value ? props.timestamps.slice(0, SHOW_COUNT) : props.timestamps,
);

const isClosedTime = (timestamp: ITimeSlot): boolean =>
  isClosedSlotStatus(timestamp.status);

const changeHandler = (): void => {
  emit('select', checkedTime.value);
};

const toggleTimeStamps = (): void => {
  isCollapsed.value = !isCollapsed.value;
};

const selectFirstOpen = (): void => {
  const firstOpen = props.timestamps.find((el) => el.status === SCHEDULE_STATUS_OPEN);
  checkedTime.value = firstOpen?.time ?? '';
  changeHandler();
};

onMounted(selectFirstOpen);

watch(
  () => props.timestamps,
  () => {
    isCollapsed.value = props.timestamps.length > SHOW_COUNT;
    selectFirstOpen();
  },
);
</script>

<template>
  <div :class="['time-selector', { _mobile: isMobile }, { _calendar: isCalendar }]">
    <div
      v-for="timestamp in times"
      :key="timestamp.time"
      :class="['time-selector__item', { _closed: isClosedTime(timestamp) }]"
    >
      <input
        :id="`time-selector-${timestamp.time}${isCalendar ? '-calendar' : ''}`"
        v-model="checkedTime"
        type="radio"
        :name="`time${isCalendar ? '-calendar' : ''}`"
        :value="timestamp.time"
        class="time-selector__input"
        @change="changeHandler"
      />
      <label
        class="time-selector__label"
        :for="`time-selector-${timestamp.time}${isCalendar ? '-calendar' : ''}`"
      >
        {{ timestamp.time }}
      </label>
    </div>
    <div v-if="timestamps.length > SHOW_COUNT" class="time-selector__item">
      <button
        type="button"
        :class="['time-selector__button', { _full: !isCollapsed }]"
        :aria-label="isCollapsed ? 'Показать все слоты' : 'Свернуть слоты'"
        @click.prevent="toggleTimeStamps"
      ></button>
    </div>
  </div>
</template>

<style lang="less">
.time-selector {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  grid-column-gap: 3px;
  grid-row-gap: 9px;

  &__item {
    &._closed {
      pointer-events: none;

      .time-selector__label {
        border-color: @color-alto;
        color: @color-alto;
      }

      .time-selector__input[type='radio']:checked + label {
        background: white;
        border-color: @color-alto;
        box-shadow: none;
      }
    }
  }

  &__input {
    display: none;
  }

  &__label {
    height: @size-x10;
    display: flex;
    justify-content: center;
    align-items: center;
    background: white;
    border: 1px solid @color-gallery;
    cursor: pointer;
    user-select: none;
    border-radius: @main-size;
    width: 100%;
    font-weight: 500;
    font-size: @size-x3;
    line-height: @size-x4;
    color: @color-elephant;
  }

  &__button {
    border: 1px solid @color-gallery;
    border-radius: @main-size;
    height: @size-x10;
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    &::after {
      content: '';
      display: block;
      border-bottom: 2px solid @color-elephant;
      border-right: 2px solid @color-elephant;
      width: @size-x3;
      height: @size-x3;
      border-radius: 2px;
      transform: rotate(45deg);
    }

    &._full::after {
      transform: rotate(-135deg);
    }
  }

  &__input[type='radio']:checked + label {
    background: @color-sauvignon;
    border-color: @color-persimmon;
    box-shadow:
      -@main-size -5px 25px rgba(0, 0, 0, 0.06),
      0px @size-x10 @size-x10 -18px rgba(139, 144, 164, 0.2);
  }

  &._mobile {
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: @size-x2;
    padding-bottom: @size-x4;
    margin-bottom: -@size-x4;
  }

  &._mobile &__label {
    width: 100%;
  }

  &._mobile &__button {
    &::after {
      width: 10px;
      height: 10px;
    }
  }
}
</style>
